import { useLazyInvestmentEntityStore, useLazyRedemptionRequestStore } from '@stores/index';
import { FundriseRouteConfig } from 'vue-router';

/* eslint-disable @typescript-eslint/explicit-function-return-type */

const redemptionRequestRoutes: Array<FundriseRouteConfig> = [
	{
		path: '/reits/redemptions/request/',
		name: 'redemption-request-REIT',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import('@views/redemption-request/redemption-request.vue'),
		beforeEnter: async () => {
			const redemptionRequestStore = await useLazyRedemptionRequestStore();
			const investmentEntityStore = await useLazyInvestmentEntityStore();

			await redemptionRequestStore.fetchRedemptionRequestInterstitialVariant();
			redemptionRequestStore.redemptionType = 'REIT';
			await redemptionRequestStore.fetchAndStoreMetadata();
			await investmentEntityStore.storeBankAccounts();
		},
		redirect: { name: 'redemption-request-start-REIT' },
		children: [
			{
				path: 'start',
				name: 'redemption-request-start-REIT',
				meta: { step: 1, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-start.vue')
			},
			{
				path: 'survey',
				name: 'redemption-request-survey-REIT',
				meta: { step: 2, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-survey.vue')
			},
			{
				path: 'message',
				name: 'redemption-request-interstitial-REIT',
				meta: { step: 3, minTokenScopeRequired: 'FULL_ACCESS', suppressDefaultMxpViewEvent: true },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					await redemptionRequestStore.fetchRedemptionRequestInterstitialVariant();

					if (redemptionRequestStore.showRedemptionRequestInterstitial) {
						if (redemptionRequestStore.completedSurvey) {
							return;
						} else {
							return { name: 'redemption-request-survey-REIT', replace: true };
						}
					} else {
						return { name: 'redemption-request-REIT' };
					}
				},
				component: () => import('@views/redemption-request/redemption-request-interstitial.vue')
			},
			{
				path: 'request-form',
				name: 'redemption-request-form-REIT',
				meta: { step: 4, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (redemptionRequestStore.redemptionPreference === 'FULL') {
						return { name: 'redemption-request-review-REIT', replace: true };
					} else if (!redemptionRequestStore.hasRedeemableReits) {
						return { name: 'redemption-request-start-REIT', replace: true };
					} else if (redemptionRequestStore.submitted) {
						return { name: 'account-overview', replace: true };
					} else if (!redemptionRequestStore.completedSurvey) {
						return { name: 'redemption-request-survey-REIT', replace: true };
					} else {
						return;
					}
				},
				component: () => import('@views/redemption-request/redemption-request-form-reit.vue')
			},
			{
				path: 'review',
				name: 'redemption-request-review-REIT',
				meta: { step: 5, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (!redemptionRequestStore.hasRedeemableReits || !redemptionRequestStore.hasRequests) {
						return { name: 'redemption-request-start-REIT', replace: true };
					} else if (redemptionRequestStore.submitted) {
						return { name: 'account-overview', replace: true };
					} else {
						redemptionRequestStore.storeRedemptionPreview();
						redemptionRequestStore.showProceeds = false;
						return;
					}
				},
				component: () => import('@views/redemption-request/redemption-request-review.vue')
			},
			{
				path: 'acknowledgements',
				name: 'redemption-request-agreements-REIT',
				meta: { step: 6, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (!redemptionRequestStore.completedSurvey) {
						return { name: 'redemption-request-survey-REIT', replace: true };
					} else if (!redemptionRequestStore.hasRequests) {
						return { name: 'redemption-request-start-REIT', replace: true };
					} else if (redemptionRequestStore.submitted) {
						return { name: 'account-overview', replace: true };
					} else {
						return;
					}
				},
				component: () => import('@views/redemption-request/redemption-request-agreements.vue')
			},
			{
				path: 'success',
				name: 'redemption-request-success-REIT',
				meta: { step: 7, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (
						redemptionRequestStore.redemptionPreview === null ||
						redemptionRequestStore.redemptionRequestGroupId === ''
					) {
						return { name: 'redemption-request-start-REIT', replace: true };
					} else {
						return;
					}
				},
				component: () => import('@views/redemption-request/redemption-request-success.vue')
			},
			{
				path: 'form',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				redirect: { name: 'redemption-request-start-REIT' }
			}
		]
	},
	{
		path: '/ipo/redemptions/request/',
		name: 'redemption-request-IPO',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import('@views/redemption-request/redemption-request.vue'),
		beforeEnter: async () => {
			const redemptionRequestStore = await useLazyRedemptionRequestStore();
			const investmentEntityStore = await useLazyInvestmentEntityStore();

			redemptionRequestStore.redemptionType = 'IPO';
			await redemptionRequestStore.fetchAndStoreMetadata();
			await investmentEntityStore.storeBankAccounts();
		},
		redirect: { name: 'redemption-request-start-IPO' },
		children: [
			{
				path: 'start',
				name: 'redemption-request-start-IPO',
				meta: { step: 1, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-start.vue')
			},
			{
				path: 'form',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				redirect: { name: 'redemption-request-start-IPO' }
			},
			{
				path: 'message',
				name: 'redemption-request-interstitial-IPO',
				meta: { step: 2, minTokenScopeRequired: 'FULL_ACCESS', suppressDefaultMxpViewEvent: true },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					await redemptionRequestStore.fetchRedemptionRequestInterstitialVariant();

					if (redemptionRequestStore.showRedemptionRequestInterstitial) {
						return;
					} else {
						return { name: 'redemption-request-IPO' };
					}
				},
				component: () => import('@views/redemption-request/redemption-request-interstitial.vue')
			},
			{
				path: 'redemption-preference',
				name: 'redemption-request-preference-IPO',
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (!redemptionRequestStore.hasRedeemableReits) {
						return { name: 'redemption-request-start-IPO', replace: true };
					} else if (redemptionRequestStore.submitted) {
						return { name: 'account-overview', replace: true };
					} else {
						return;
					}
				},
				meta: { step: 3, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-preference.vue')
			},
			{
				path: 'request-form',
				name: 'redemption-request-form-IPO',
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (redemptionRequestStore.redemptionPreference === 'FULL') {
						return { name: 'redemption-request-agreements-IPO', replace: true };
					} else if (!redemptionRequestStore.hasRedeemableReits) {
						return { name: 'redemption-request-start-IPO', replace: true };
					} else if (redemptionRequestStore.submitted) {
						return { name: 'account-overview', replace: true };
					} else {
						return;
					}
				},
				meta: { step: 4, minTokenScopeRequired: 'FULL_ACCESS' },
				component: () => import('@views/redemption-request/redemption-request-form-ipo.vue')
			},
			{
				path: 'acknowledgements',
				name: 'redemption-request-agreements-IPO',
				meta: { step: 5, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (!redemptionRequestStore.hasRequests) {
						return { name: 'redemption-request-start-IPO', replace: true };
					} else if (redemptionRequestStore.submitted) {
						return { name: 'account-overview', replace: true };
					} else {
						await redemptionRequestStore.storeRedemptionPreview();
						redemptionRequestStore.showProceeds = false;
						return;
					}
				},
				component: () => import('@views/redemption-request/redemption-request-agreements.vue')
			},
			{
				path: 'success',
				name: 'redemption-request-success-IPO',
				meta: { step: 6, minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async () => {
					const redemptionRequestStore = await useLazyRedemptionRequestStore();

					if (
						redemptionRequestStore.redemptionPreview === null ||
						redemptionRequestStore.redemptionRequestGroupId === ''
					) {
						return { name: 'redemption-request-start-IPO', replace: true };
					} else {
						return;
					}
				},
				component: () => import('@views/redemption-request/redemption-request-success.vue')
			}
		]
	}
];
export default redemptionRequestRoutes;
